import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { useHistory, useLocation } from 'react-router-dom';

import useMfrProducts from 'components/upb_mfr_viewer/hooks/useMfrProducts';
import BetaPagination from 'components/upb_viewer_beta/BetaPagination';
import BetaPaneSelect from 'components/upb_viewer_beta/BetaPaneSelect';
import BetaProductGrid from 'components/upb_viewer_beta/BetaProductGrid';
import {
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_MEDIUM,
} from 'components/upb_viewer_beta/css/betaTokens';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';
import { ProductType } from 'lib/enums/ProductType';
import { addReferrerToUrl } from 'lib/utils/navigationUtils';

// SKU-type and sort options mirror the UPB beta viewer's product listing
// (BetaProductsPage) verbatim so the two viewers read and behave identically.
const SKU_TYPES = [
  { value: 'total', label: 'Total SKUs' },
  { value: 'active', label: 'Active SKUs' },
  { value: 'gmv', label: 'GMV SKUs' },
];

const SORT_OPTIONS = [
  { value: 'annualized_gmv', label: 'Annualized GMV' },
  { value: 'market_share', label: 'Market Share' },
  { value: 'name', label: 'Name (A→Z)' },
  { value: 'dp_count', label: 'DPs' },
  { value: 'attribute_score', label: 'Attribute Score' },
];

/**
 * MfrProductsPage — MSV brand-detail grid: the brand's products ordered by GMV
 * %. Reuses the beta product card/grid; the /products payload exposes l5_cat_gmv
 * (mapped to the card's annualized_gmv) and gmv_percentage (market share).
 *
 * The SKU-type filter and Sort dropdown match the UPB beta viewer's controls
 * row. Both are applied CLIENT-SIDE to the current page only: the backing route
 * (GET /api/v1/annualized-mfr-gmv/products) accepts brand_id / limit / offset /
 * search_query and nothing else — its ordering is hard-coded to
 * `ORDER BY gmv_percentage DESC, NAME ASC` — so there is no server-side sort or
 * sku_type param to delegate to. Same known limitation the UPB viewer carries:
 * the headline count stays server-side while the grid shows the filtered subset
 * of this page, so the count line calls out how many survived on this page.
 */
function MfrProductsPage({ brandId, searchQuery }) {
  const history = useHistory();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  // Defaults chosen so the first render is identical to the pre-existing screen:
  // 'total' applies no filter, and 'market_share' reproduces the server's
  // ORDER BY gmv_percentage DESC exactly. The user opts into the rest.
  const [skuType, setSkuType] = useState('total');
  const [sortKey, setSortKey] = useState('market_share');
  const page = useMemo(() => Math.max(currentPage - 1, 0), [currentPage]);
  const offset = useMemo(() => page * DEFAULT_UPB_PAGE_SIZE, [page]);

  const trimmedSearch = (searchQuery || '').trim();
  const isSearching = trimmedSearch.length > 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [brandId, searchQuery]);

  const { data: { data: products = [], count = 0 } = {}, loading } = useMfrProducts({
    brandId,
    offset,
    limit: DEFAULT_UPB_PAGE_SIZE,
    searchQuery,
  });

  // Map the mfr-GMV product payload onto the fields BetaProductCard reads:
  // l5_cat_gmv → annualized_gmv ($ shown), gmv_percentage → market share.
  const cardProducts = useMemo(
    () => products.map(p => ({ ...p, annualized_gmv: p.l5_cat_gmv })),
    [products],
  );

  // Client-side SKU-type filter + sort over the current page (see the component
  // docblock for why this cannot be pushed to the server today).
  const visibleProducts = useMemo(() => {
    // Search results arrive relevance-ranked (semantic search) from the backend,
    // so leave their order alone and apply no browse-only filter or sort.
    if (isSearching) return cardProducts;
    let list = cardProducts;
    // dp_count and gmv_percentage are COALESCE(..., 0) in the products query, so
    // both are always present on every row — a plain `> 0` test is unambiguous.
    //   - 'active' keeps products carried by at least one distributor.
    //   - 'gmv'    keeps products with any GMV share.
    if (skuType === 'active') {
      list = list.filter(p => Number(p.dp_count) > 0);
    } else if (skuType === 'gmv') {
      list = list.filter(p => Number(p.gmv_percentage) > 0);
    }
    const sorted = [...list];
    const num = v => (v == null ? -Infinity : Number(v));
    const cmp = {
      annualized_gmv: (a, b) => num(b.annualized_gmv) - num(a.annualized_gmv),
      market_share: (a, b) => num(b.gmv_percentage) - num(a.gmv_percentage),
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      dp_count: (a, b) => num(b.dp_count) - num(a.dp_count),
      attribute_score: (a, b) => num(b.fsa_score) - num(a.fsa_score),
    };
    if (cmp[sortKey]) sorted.sort(cmp[sortKey]);
    return sorted;
  }, [cardProducts, isSearching, skuType, sortKey]);

  const totalPages = Math.max(Math.ceil(count / DEFAULT_UPB_PAGE_SIZE), 1);
  const startIdx = count === 0 ? 0 : offset + 1;
  const endIdx = Math.min(offset + DEFAULT_UPB_PAGE_SIZE, count);
  const filteredOut = products.length - visibleProducts.length;

  const handleProductClick = product => {
    const productId = product.global_product_id || product.id;
    const base = `/product-details/${productId}/${ProductType.GLOBAL}`;
    history.push(addReferrerToUrl(base, `${location.pathname}${location.search}`));
  };

  return (
    <div className={css(styles.page)}>
      {/* Breadcrumb + title + distributor filter live in the container's header
          card (mock); this row carries the count plus the SKU-type and Sort
          dropdowns — same controls row as the UPB beta viewer. */}
      <div className={css(styles.head)}>
        <span className={css(styles.count)}>
          {count === 0
            ? 'No products'
            : `${startIdx.toLocaleString('en-US')}–${endIdx.toLocaleString(
                'en-US',
              )} of ${count.toLocaleString('en-US')} products${
                filteredOut > 0
                  ? ` (${visibleProducts.length.toLocaleString('en-US')} on this page)`
                  : ''
              }`}
        </span>
        {/* Browse-only controls: hidden in search mode, where the backend's
            relevance ranking owns the order (mirrors BetaProductsPage). */}
        {!isSearching && (
          <BetaPaneSelect
            caption="SKU type"
            options={SKU_TYPES}
            value={skuType}
            onChange={setSkuType}
            menuWidth={240}
            minWidth={170}
            compact
          />
        )}
        {!isSearching && (
          <BetaPaneSelect
            caption="Sort"
            options={SORT_OPTIONS}
            value={sortKey}
            onChange={setSortKey}
            menuWidth={240}
            minWidth={200}
            compact
          />
        )}
      </div>

      <BetaProductGrid
        products={visibleProducts}
        loading={loading}
        onProductClick={handleProductClick}
      />

      {totalPages > 1 && (
        <BetaPagination
          totalPages={totalPages}
          currentPageNumber={page}
          onPageChange={newPage => setCurrentPage(newPage + 1)}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },

  // Controls row: count pushed left (marginRight: auto on .count), dropdowns
  // trailing right — identical treatment to BetaProductsPage's controls row.
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },

  count: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
    marginRight: 'auto',
  },
});

MfrProductsPage.propTypes = {
  brandId: PropTypes.number,
  searchQuery: PropTypes.string,
};

MfrProductsPage.defaultProps = {
  brandId: null,
  searchQuery: '',
};

export default MfrProductsPage;
